import { useEffect, useCallback } from 'react';
import { useLocation } from 'react-router';
import { AppRoutes } from '@/constants';
import { selectCaseMetadata, useAppSelector } from '@/store';
import { useLazyGetCaseQuery } from '@/api';

type BundlePageType =
  | typeof AppRoutes.FullBundle
  | typeof AppRoutes.LightBundle
  | typeof AppRoutes.Timeline
  | typeof AppRoutes.Chronology;

const isBundlePage = (page: string): page is BundlePageType => {
  return [AppRoutes.FullBundle, AppRoutes.LightBundle, AppRoutes.Timeline, AppRoutes.Chronology].includes(
    page as BundlePageType,
  );
};

const useRouteTitle = () => {
  const caseMetadata = useAppSelector(selectCaseMetadata);
  const location = useLocation();
  const { pathname } = location;
  const [triggerGetCase] = useLazyGetCaseQuery();

  const setDocumentTitle = useCallback((pageType: BundlePageType, caseReference: string) => {
    if (!caseReference) return;

    const titleMap: Record<BundlePageType, string> = {
      [AppRoutes.FullBundle]: `Full Bundle - ${caseReference}`,
      [AppRoutes.LightBundle]: `Light Bundle - ${caseReference}`,
      [AppRoutes.Timeline]: `Timeline - ${caseReference}`,
      [AppRoutes.Chronology]: `Chronology - ${caseReference}`,
    };

    document.title = titleMap[pageType];
  }, []);

  useEffect(() => {
    if (!pathname) return;

    // Calculate segments inside the effect to avoid dependency on a new array each render
    const segments = pathname.split('/').filter(Boolean);

    // Login route
    if (pathname === '/login' || pathname === `/${AppRoutes.Login}`) {
      document.title = 'Login';
      return;
    }

    // Case List route
    else if (segments.length === 1 && pathname === `/${AppRoutes.CaseList}`) {
      document.title = 'Case List';
      return;
    }

    // Medical Records route
    else if (segments.length === 1 && pathname === `/${AppRoutes.MedicalRecords}`) {
      document.title = 'Medical Records';
      return;
    }

    // Case Item route
    else if (
      segments.length === 3 &&
      segments[0] === AppRoutes.CaseList &&
      (segments[2] === AppRoutes.UploadedMedicalRecords || segments[2] === AppRoutes.AiProcessedDocuments)
    ) {
      if (caseMetadata?.caseReference) {
        document.title = `Case - ${caseMetadata.caseReference}`;
      }
      return;
    }

    // Bundle Pages route
    else if (
      segments.length === 5 &&
      segments[0] === AppRoutes.CaseList &&
      segments[2] === AppRoutes.AiProcessedDocuments &&
      isBundlePage(segments[3])
    ) {
      const caseId = segments[1];
      const pageType = segments[3] as BundlePageType;

      const handleCaseMetadata = (metadata?: { caseReference?: string }) => {
        if (metadata?.caseReference) {
          setDocumentTitle(pageType, metadata.caseReference);
        }
      };

      // If caseMetadata is not available or does not match the current caseId, fetch it
      if (!caseMetadata || caseMetadata.caseId !== caseId) {
        // Set a default title before data loading if no case data is available
        setDocumentTitle(pageType, 'Case');

        triggerGetCase({ caseId })
          .unwrap()
          .then((result) => {
            if (result?.info) {
              handleCaseMetadata(result.info);
            } else {
              console.error('Invalid response structure');
            }
          })
          .catch((error) => {
            console.error('Failed to fetch case:', error);
          });
      } else {
        handleCaseMetadata(caseMetadata ?? undefined);
      }
    }

    //Active Users route
    else if (
      segments.length === 2 &&
      segments[0] === AppRoutes.UserManagement &&
      segments[1] === AppRoutes.ActiveUsers
    ) {
      document.title = 'Active Users';
      return;
    }

    // Deactivated Users route
    else if (
      segments.length === 2 &&
      segments[0] === AppRoutes.UserManagement &&
      segments[1] === AppRoutes.DeactivatedUsers
    ) {
      document.title = 'Deactivated Users';
      return;
    }

    // Global Admin Dashboard route
    else if (segments.length === 1 && segments[0] === AppRoutes.GlobalAdminDashboard) {
      document.title = 'Global Admin Dashboard';
      return;
    } else {
      document.title = 'Mermaidd';
    }
  }, [pathname, caseMetadata, triggerGetCase, setDocumentTitle]);
};

export { useRouteTitle };
